import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  IconButton,
  Container,
  alpha,
  useTheme
} from '@mui/material';
import { 
  Payment as PaymentIcon,
  Person as PersonIcon,
  FamilyRestroom as FamilyRestroomIcon,
  MedicalServices as MedicalServicesIcon,
  Gavel as GavelIcon,
  ContactEmergency as ContactEmergencyIcon,
  Notes as NotesIcon,
  Save as SaveIcon,
  Draw as DrawIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import apiService from '../services/api';
import { Member, EstateAddress, AvailableEstateAddressesResponse } from '../types';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Membership pricing configuration - updated for new membership structure
const membershipPricing = {
  'Home Owner (H/O)': { monthly: 540, annual: 6480 },
  'Tenant': { monthly: 450, annual: 5400 },
  'Outside Member': {
    'Single': {
      contracts: {
        '12 Months': { monthly: 540, upfront: 5832 },
        '6 Months': { monthly: 594, upfront: 3240 },
        '1 Month': { monthly: 702 },
        '2 Weeks': { amount: 438.75 }
      }
    },
    'Family': {
      contracts: {
        '12 Months': { monthly: 1500, upfront: 16200 },
        '6 Months': { monthly: 1650, upfront: 9000 },
        '1 Month': { monthly: 1950 },
        '2 Weeks': { amount: 1218.75 }
      }
    },
    'Scholars (u18 yrs)': {
      contracts: {
        '12 Months': { monthly: 390, upfront: 4212 },
        '6 Months': { monthly: 429, upfront: 2340 },
        '1 Month': { monthly: 507 },
        '2 Weeks': { amount: 316.88 }
      }
    },
    'Parent and child (u18 yrs)': {
      contracts: {
        '12 Months': { monthly: 850, upfront: 9180 },
        '6 Months': { monthly: 935, upfront: 5100 },
        '1 Month': { monthly: 1105 },
        '2 Weeks': { amount: 690.63 }
      }
    },
    'Pensioner': {
      contracts: {
        '12 Months': { monthly: 390, upfront: 4212 },
        '6 Months': { monthly: 429, upfront: 2340 },
        '1 Month': { monthly: 507 },
        '2 Weeks': { amount: 316.88 }
      }
    },
    'Police': {
      contracts: {
        '12 Months': { monthly: 420, upfront: 4536 },
        '6 Months': { monthly: 462, upfront: 2520 },
        '1 Month': { monthly: 546 },
        '2 Weeks': { amount: 341.25 }
      }
    }
  }
};

interface MemberFormData {
  // Membership Details
  rate: number;
  membershipPosition: 'Home Owner (H/O)' | 'Tenant' | 'Outside Member'; // Updated to match AddMember structure
  membershipType?: 'Single' | 'Family' | 'Scholars (u18 yrs)' | 'Parent and child (u18 yrs)' | 'Pensioner' | 'Police'; // Only for Outside Members
  contractLength?: string; // New field for contract length
  expiryDate: string;
  paymentDay: '25th' | '1st';
  paymentFrequency: 'Monthly' | 'Upfront';
  paymentType: 'Debit Order' | 'EFT/Cash';
  accountReference: string;
  proRata: number;
  joiningFee: number;
  accessFee: number;
  membershipStatus: 'active' | 'inactive' | 'suspended' | 'expired';
  startDate: string;
  endDate: string;
  paymentStatus: 'current' | 'overdue' | 'cancelled';
  isActive: string;
  
  // Member's Particulars
  firstName: string;
  lastName: string;
  email: string;
  address: string; // Updated to single address field like AddMember
  phone: {
    mobile: string;
    work: string;
  };
  dateOfBirth: string;
  idNumber: string;
  employer: {
    name: string;
    address: string;
  };
  businessTel: string;
  occupation: string;
  
  // Emergency Contact
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  
  // Spouse's Particulars
  spouse: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    idNumber: string;
    occupation: string;
  };
  
  // Children's Particulars
  children: {
    name: string;
    dateOfBirth: string;
    idNumber: string;
  }[];
  
  // Debit Order Authority
  debitOrder: {
    accountHolder: string;
    address: string;
    contactNumbers: {
      cell: string;
      work: string;
    };
    bank: string;
    branchCode: string;
    accountNumber: string;
    accountType: 'Current' | 'Savings' | 'Transmission';
    amountToDeduct: number;
    debitOrderDate: string;
  };
  
  // Indemnity & Medical Info
  medicalInfo: {
    allergies: string;
    medication: string;
    recentSurgery: string;
    doctorName: string;
    doctorNumber: string;
    emergencyContact: string;
  };
  
  // Signatures
  signedAt: string;
  signedDate: string;
  signedMonth: string;
  signature: string;
  
  // Legal Acknowledgements
  legalAcknowledgements: {
    mandate: boolean;
    cancellation: boolean;
    assignment: boolean;
    popiAct: boolean;
    indemnity: boolean;
  };
  
  notes: string;
  tags?: string[];
  estateAddressId: string;
}

const EditMember: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [member, setMember] = useState<Member | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();

  // Estate Address state
  const [villages, setVillages] = useState<string[]>([]);
  const [availableAddresses, setAvailableAddresses] = useState<EstateAddress[]>([]);
  const [selectedVillage, setSelectedVillage] = useState<string>('');
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<MemberFormData>({
    defaultValues: {
      rate: 0,
      membershipPosition: 'Outside Member',
      membershipType: 'Single',
      contractLength: '12 Months',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      paymentDay: '1st',
      paymentFrequency: 'Monthly',
      paymentType: 'Debit Order',
      accountReference: '',
      proRata: 0,
      joiningFee: 0,
      accessFee: 0,
      membershipStatus: 'active',
      paymentStatus: 'current',
      isActive: 'true',

      // Member particulars defaults
      firstName: '',
      lastName: '',
      email: '',
      address: '',
      estateAddressId: '',
      phone: { mobile: '', work: '' },
      dateOfBirth: '',
      idNumber: '',
      employer: { name: '', address: '' },

      // Emergency contact defaults
      emergencyContact: { name: '', phone: '', relationship: '' },

      // Spouse defaults
      spouse: { firstName: '', lastName: '', email: '', phone: '', idNumber: '', occupation: '' },

      // Children
      children: [{ name: '', dateOfBirth: '', idNumber: '' }],

      // Debit order
      debitOrder: { accountHolder: '', address: '', contactNumbers: { cell: '', work: '' }, bank: '', branchCode: '', accountNumber: '', accountType: 'Current', amountToDeduct: 0, debitOrderDate: '' },

      medicalInfo: { allergies: '', medication: '', recentSurgery: '', doctorName: '', doctorNumber: '', emergencyContact: '' },

      // Signatures and legal
      signedAt: '', signedDate: new Date().toISOString().split('T')[0], signedMonth: '', signature: '',
      legalAcknowledgements: { mandate: false, cancellation: false, assignment: false, popiAct: false, indemnity: false },

  notes: '', tags: [] ,
    }
  });

  const { fields: childrenFields, append: appendChild, remove: removeChild } = useFieldArray({
    control,
    name: 'children',
  });

  // Watch the membership position, membership type, contract length, and payment frequency for auto-rate calculation
  const currentMembershipPosition = watch('membershipPosition');
  const currentMembershipType = watch('membershipType');
  const currentContractLength = watch('contractLength');

  const currentPaymentFrequency = watch('paymentFrequency');
  const currentPaymentType = watch('paymentType');
  const currentRate = watch('rate');

  // Function to extract date of birth from South African ID number
  const extractDateOfBirthFromId = (idNumber: string): string => {
    // Only extract date from SA ID numbers (13 digits)
    if (!idNumber || idNumber.length !== 13 || !/^\d{13}$/.test(idNumber)) {
      return '';
    }

    // Extract YYMMDD from the first 6 digits
    const yearPart = idNumber.substring(0, 2);
    const monthPart = idNumber.substring(2, 4);
    const dayPart = idNumber.substring(4, 6);

    // Convert 2-digit year to 4-digit year
    // If year is 00-25, assume 2000-2025, otherwise assume 1900-1999
    const century = parseInt(yearPart) <= 25 ? 2000 : 1900;
    const fullYear = century + parseInt(yearPart);

    // Validate the date components
    const month = parseInt(monthPart);
    const day = parseInt(dayPart);

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return '';
    }

    // Create date string in YYYY-MM-DD format
    const dateString = `${fullYear}-${monthPart.padStart(2, '0')}-${dayPart.padStart(2, '0')}`;

    // Validate that the date is valid
    const date = new Date(dateString);
    if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return '';
    }

    return dateString;
  };

  // Auto-calculate rate when membership position, type, contract length, or payment frequency changes
  useEffect(() => {
    if (currentMembershipPosition) {
      let correctRate = 0;
      let suggestedPaymentFrequency = currentPaymentFrequency;
      
      // Handle Home Owner and Tenant (legacy pricing)
      if (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant') {
        const pricing = membershipPricing[currentMembershipPosition];
        correctRate = currentPaymentFrequency === 'Monthly' ? pricing.monthly : pricing.annual;
      }
      // Handle Outside Member (contract-based pricing)
      else if (currentMembershipPosition === 'Outside Member' && currentMembershipType && currentContractLength) {
        const outsideMemberPricing = membershipPricing['Outside Member'];
        const typePricing = outsideMemberPricing[currentMembershipType];
        if (typePricing && typePricing.contracts) {
          const contractPricing = typePricing.contracts[currentContractLength as keyof typeof typePricing.contracts];
          if (contractPricing) {
            // Auto-set payment frequency based on contract type
            if (currentContractLength === '2 Weeks') {
              // 2 Weeks only has single amount - set to Monthly as default but will show as read-only
              suggestedPaymentFrequency = 'Monthly';
            } else if (currentContractLength === '1 Month') {
              // 1 Month only has monthly option
              suggestedPaymentFrequency = 'Monthly';
            } else if (!currentPaymentFrequency) {
              // Default to Monthly for contracts that support both
              if ('monthly' in contractPricing) {
                suggestedPaymentFrequency = 'Monthly';
              } else if ('upfront' in contractPricing) {
                suggestedPaymentFrequency = 'Upfront';
              }
            }
            
            // Update payment frequency if it changed
            if (suggestedPaymentFrequency !== currentPaymentFrequency) {
              setValue('paymentFrequency', suggestedPaymentFrequency);
            }
            
            // Calculate rate based on selected payment frequency
            if (currentPaymentFrequency === 'Monthly' && 'monthly' in contractPricing) {
              correctRate = contractPricing.monthly;
            } else if (currentPaymentFrequency === 'Upfront' && 'upfront' in contractPricing) {
              correctRate = contractPricing.upfront;
            } else if ('amount' in contractPricing) {
              // For contracts with only amount (like 2 Weeks)
              correctRate = contractPricing.amount;
            } else if ('monthly' in contractPricing) {
              // Fallback to monthly if available
              correctRate = contractPricing.monthly;
            }
          }
        }
      }
      
      if (correctRate > 0) {
        setValue('rate', correctRate);
      }
      
      // Auto-calculate expiry date based on membership position and contract length
      if (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant') {
        // Set expiry date to 50 years in the future for Home Owner and Tenant
        const today = new Date();
        const expiryDate = new Date(today);
        expiryDate.setFullYear(today.getFullYear() + 50);
        setValue('endDate', expiryDate.toISOString().split('T')[0]);
      } else if (currentMembershipPosition === 'Outside Member' && currentContractLength) {
        const today = new Date();
        let expiryDate = new Date(today);
        
        switch (currentContractLength) {
          case '12 Months':
            expiryDate.setFullYear(today.getFullYear() + 1);
            break;
          case '6 Months':
            expiryDate.setMonth(today.getMonth() + 6);
            break;
          case '1 Month':
            expiryDate.setMonth(today.getMonth() + 1);
            break;
          case '2 Weeks':
            expiryDate.setDate(today.getDate() + 14);
            break;
          default:
            expiryDate.setFullYear(today.getFullYear() + 1);
        }
        
        setValue('endDate', expiryDate.toISOString().split('T')[0]);
      }
    }
  }, [currentMembershipPosition, currentMembershipType, currentContractLength, currentPaymentFrequency, setValue]);
  
  // Function to calculate pro rata based on current date, rate, and payment frequency
  const calculateProRata = (rate: number, paymentFrequency: 'Monthly' | 'Upfront'): number => {
    if (!rate || rate <= 0) return 0;
    
    if (paymentFrequency === 'Monthly') {
      // Calculate pro rata for the current month
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      // Get the last day of current month
      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      
      // Get current day of month
      const currentDay = today.getDate();
      
      // Calculate remaining days in the month (including today)
      const remainingDays = lastDayOfMonth - currentDay + 1;
      
      // Calculate pro rata amount
      const dailyRate = rate / lastDayOfMonth;
      const proRataAmount = dailyRate * remainingDays;
      
      return Math.round(proRataAmount * 100) / 100; // Round to 2 decimal places
    } else if (paymentFrequency === 'Upfront') {
      // For upfront payments, use the full rate (no pro rata calculation needed)
      return rate;
    }
    
    return 0;
  };
  
  // Auto-calculate pro rata when rate or payment frequency changes
  useEffect(() => {
    if (currentRate && currentRate > 0 && currentPaymentFrequency) {
      const proRataAmount = calculateProRata(currentRate, currentPaymentFrequency);
      console.log(`Pro rata calculation: Rate=${currentRate}, Frequency=${currentPaymentFrequency}, Result=${proRataAmount}`);
      setValue('proRata', proRataAmount);
    }
  }, [currentRate, currentPaymentFrequency, setValue]);

  // Load available estate addresses on component mount
  useEffect(() => {
    const loadEstateAddresses = async () => {
      try {
        setLoadingAddresses(true);
        const response: AvailableEstateAddressesResponse = await apiService.getAvailableEstateAddresses();
        setAvailableAddresses(response.addresses);
        setVillages(response.villages);
      } catch (error) {
        console.error('Error loading estate addresses:', error);
      } finally {
        setLoadingAddresses(false);
      }
    };

    // Load all estate addresses for Home Owners and Tenants (simplified - no village filtering)
    // Only auto-load available addresses when NOT editing an existing member (no `id`),
    // otherwise editing flow will fetch addresses including occupied entries for the member's village.
    if (!id && (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant')) {
      loadEstateAddresses();
    }
  }, [currentMembershipPosition, id]);

  // Filter addresses when village selection changes
  useEffect(() => {
    const filterAddressesByVillage = async () => {
      if (!selectedVillage || (currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant')) {
        return;
      }

      try {
        setLoadingAddresses(true);
        // In EditMember we need to include occupied addresses so the member's current house shows up
        const response = await apiService.getEstateAddressesByVillage(selectedVillage, true);
        setAvailableAddresses(response.addresses);
      } catch (error) {
        console.error('Error filtering addresses by village:', error);
      } finally {
        setLoadingAddresses(false);
      }
    };

    if (selectedVillage) {
      filterAddressesByVillage();
    }
  }, [selectedVillage, currentMembershipPosition]);

  const loadMember = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await apiService.getMember(id!);
      console.log('Member data for edit:', response);
      const memberData = response; // Response is the member data directly
      setMember(memberData);
      
      // Format phone depending on data structure
      const phoneData = typeof memberData.phone === 'string' 
        ? { mobile: memberData.phone, work: '' }
        : {
            mobile: memberData.phone?.mobile || '',
            work: memberData.phone?.work || ''
          };
      
      // Format address - combine all address parts into a single string for the simplified form
      const addressString = memberData.address 
        ? `${memberData.address.street || ''}, ${memberData.address.city || ''}, ${memberData.address.state || ''}, ${memberData.address.zipCode || ''}`
        : '';
      
      // Debug dateOfBirth value
      console.log('Raw dateOfBirth from API:', memberData.dateOfBirth);
      const formattedDateOfBirth = memberData.dateOfBirth ? new Date(memberData.dateOfBirth).toISOString().split('T')[0] : '';
      console.log('Formatted dateOfBirth for form:', formattedDateOfBirth);

  // Extract estate-related fallbacks
  const villageFromData = (memberData as any).villageName || (memberData as any).estateAddress?.villageName || '';
  const estateAddressIdFromData = (memberData as any).estateAddressId || (memberData as any).estateAddress?._id || '';

  // No longer using legacy address inference for estate members

  // Function to reset form with member data
  const resetFormWithMemberData = () => {
    // Determine membershipPosition robustly: prefer explicit memberData.membershipPosition,
    // otherwise infer 'Outside Member' if membershipType matches outside-member categories,
    // else fall back to membershipType or 'Tenant'.
    const outsideTypes = ['Single','Family','Scholars (u18 yrs)','Parent and child (u18 yrs)','Pensioner','Police'];
    const membershipPositionToUse = (memberData as any).membershipPosition || (outsideTypes.includes((memberData as any).membershipType) ? 'Outside Member' : ((memberData as any).membershipType || 'Tenant'));
    reset({
          // Membership Details
        rate: memberData.rate || 0,
        membershipPosition: membershipPositionToUse,
         membershipType: memberData.membershipType === 'Single' || memberData.membershipType === 'Family' || 
                        memberData.membershipType === 'Scholars (u18 yrs)' || memberData.membershipType === 'Parent and child (u18 yrs)' || 
                        memberData.membershipType === 'Pensioner' || memberData.membershipType === 'Police' 
                        ? memberData.membershipType : undefined,
         contractLength: memberData.contractLength || '12 Months',
        expiryDate: memberData.endDate ? new Date(memberData.endDate).toISOString().split('T')[0] : '',
        paymentDay: memberData.paymentDay || '1st',
        paymentFrequency: memberData.paymentFrequency || 'Monthly',
        paymentType: memberData.paymentType || 'Debit Order',
        accountReference: memberData.accountReference || '',
        proRata: memberData.proRata || 0,
        joiningFee: memberData.joiningFee || 0,
        accessFee: memberData.accessFee || 0,
        membershipStatus: memberData.membershipStatus || 'active',
        startDate: memberData.startDate ? new Date(memberData.startDate).toISOString().split('T')[0] : '',
        endDate: memberData.endDate ? new Date(memberData.endDate).toISOString().split('T')[0] : '',
        paymentStatus: memberData.paymentStatus || 'current',
        isActive: memberData.isActive !== undefined ? (memberData.isActive ? "true" : "false") : "true",
        
        // Member's Particulars
        firstName: memberData.firstName,
        lastName: memberData.lastName,
        email: memberData.email,
        address: addressString,
   estateAddressId: estateAddressIdFromData || '',
         phone: phoneData,
        dateOfBirth: formattedDateOfBirth,
        idNumber: memberData.idNumber || '',
        employer: {
          name: memberData.employer?.name || '',
          address: memberData.employer?.address || ''
        },
        businessTel: memberData.businessTel || '',
        occupation: memberData.occupation || '',
        
        // Emergency Contact
        emergencyContact: {
          name: (memberData as any).emergencyContact?.name || '',
          phone: (memberData as any).emergencyContact?.phone || '',
          relationship: (memberData as any).emergencyContact?.relationship || '',
        },
        
        // Spouse's Particulars
        spouse: {
          firstName: memberData.spouse?.firstName || '',
          lastName: memberData.spouse?.lastName || '',
          email: memberData.spouse?.email || '',
          phone: memberData.spouse?.phone || '',
          idNumber: memberData.spouse?.idNumber || '',
          occupation: memberData.spouse?.occupation || ''
        },
        
        // Children's Particulars
        children: memberData.children && memberData.children.length > 0 
          ? memberData.children 
          : [{ name: '', dateOfBirth: '', idNumber: '' }],
        
        // Debit Order Authority
        debitOrder: {
          accountHolder: memberData.debitOrder?.accountHolder || '',
          address: memberData.debitOrder?.address || '',
          contactNumbers: {
            cell: memberData.debitOrder?.contactNumbers?.cell || '',
            work: memberData.debitOrder?.contactNumbers?.work || ''
          },
          bank: memberData.debitOrder?.bank || '',
          branchCode: memberData.debitOrder?.branchCode || '',
          accountNumber: memberData.debitOrder?.accountNumber || '',
          accountType: memberData.debitOrder?.accountType || 'Current',
          amountToDeduct: memberData.debitOrder?.amountToDeduct || 0,
          debitOrderDate: memberData.debitOrder?.debitOrderDate || ''
        },
        
        // Indemnity & Medical Info
        medicalInfo: {
          allergies: memberData.medicalInfo?.allergies || '',
          medication: memberData.medicalInfo?.medication || '',
          recentSurgery: memberData.medicalInfo?.recentSurgery || '',
          doctorName: memberData.medicalInfo?.doctorName || '',
          doctorNumber: memberData.medicalInfo?.doctorNumber || '',
          emergencyContact: memberData.medicalInfo?.emergencyContact || ''
        },
        
        // Signatures
        signedAt: memberData.signedAt || '',
        signedDate: memberData.signedDate || new Date().toISOString().split('T')[0],
        signedMonth: memberData.signedMonth || '',
        signature: memberData.signature || '',
        
        // Legal Acknowledgements
        legalAcknowledgements: {
          mandate: memberData.legalAcknowledgements?.mandate || false,
          cancellation: memberData.legalAcknowledgements?.cancellation || false,
          assignment: memberData.legalAcknowledgements?.assignment || false,
          popiAct: memberData.legalAcknowledgements?.popiAct || false,
          indemnity: memberData.legalAcknowledgements?.indemnity || false
        },
        
        notes: memberData.notes || '',
        tags: memberData.tags || [],
      });
   };

      // Handle village and estate address population for Home Owner/Tenant
      const isEstateBasedMember = memberData.membershipType === 'Home Owner (H/O)' || memberData.membershipType === 'Tenant';

      // For estate-based members, only use data if we have a proper estateAddressId
      if (isEstateBasedMember && estateAddressIdFromData) {
        // Member has a proper estate address link - use that village
        const finalVillageToUse = villageFromData || '';

        if (finalVillageToUse) {
          console.log('Setting village from member estate data:', finalVillageToUse);
          setSelectedVillage(finalVillageToUse);
          setVillages(prev => prev && prev.length > 0 ? (prev.includes(finalVillageToUse) ? prev : [finalVillageToUse, ...prev]) : [finalVillageToUse]);

          // Load estate addresses for this village
          const loadEstateAddressesForVillage = async () => {
            try {
              console.log('Loading estate addresses for village:', finalVillageToUse);
              setLoadingAddresses(true);
              const response = await apiService.getEstateAddressesByVillage(finalVillageToUse, true);
              setAvailableAddresses(response.addresses);
              console.log('Loaded estate addresses:', response.addresses);

            // Reset form with member data AFTER addresses are loaded
            resetFormWithMemberData();

            // Explicitly ensure dropdown-form controls are populated (covers mismatched names/edge cases)
            // Use sensible defaults when backend fields are missing so the Selects display correctly
            const membershipTypeValue = (memberData as any).membershipType || undefined;
            const membershipStatusValue = (memberData as any).membershipStatus || 'active';
            const paymentStatusValue = (memberData as any).paymentStatus || 'current';

            setValue('membershipType', membershipTypeValue);
            setValue('membershipStatus', membershipStatusValue);
            setValue('paymentStatus', paymentStatusValue);

            // Ensure the estateAddressId field is set explicitly (covers object vs id cases)
            if (estateAddressIdFromData) {
              setValue('estateAddressId', estateAddressIdFromData.toString());
            } else {
              // If we couldn't get an EstateAddress id, clear the field so the select shows the inferred village
              setValue('estateAddressId', '');
            }
          } catch (error) {
            console.error('Error loading estate addresses for village:', error);
            // Even if loading fails, reset the form
            resetFormWithMemberData();

            // Populate selects with fallback/default values so the UI shows consistent state
            const membershipTypeValue = (memberData as any).membershipType || undefined;
            const membershipStatusValue = (memberData as any).membershipStatus || 'active';
            const paymentStatusValue = (memberData as any).paymentStatus || 'current';

            setValue('membershipType', membershipTypeValue);
            setValue('membershipStatus', membershipStatusValue);
            setValue('paymentStatus', paymentStatusValue);

            // Still attempt to set the estateAddressId from member data so the select reflects the member's value
            if (estateAddressIdFromData) {
              setValue('estateAddressId', estateAddressIdFromData.toString());
            }
          } finally {
            setLoadingAddresses(false);
          }
        };

        loadEstateAddressesForVillage();
        }
      } else if (isEstateBasedMember) {
        // Estate-based member without estate address link - load all available addresses
        console.log('Estate-based member without estate link - loading all available addresses');
        const loadAllEstateAddresses = async () => {
          try {
            setLoadingAddresses(true);
            console.log('🏠 Loading all available estate addresses for Home Owner/Tenant assignment...');
            const response = await apiService.getAvailableEstateAddresses();
            console.log('✅ API Response:', response);

            if (response && response.addresses && response.villages) {
              setAvailableAddresses(response.addresses);
              setVillages(response.villages);
              console.log(`✅ Loaded ${response.addresses.length} addresses across ${response.villages.length} villages`);
              console.log('Villages:', response.villages);
            } else {
              console.error('❌ Invalid API response structure:', response);
              setAvailableAddresses([]);
              setVillages([]);
            }
          } catch (error) {
            console.error('❌ Error loading all estate addresses:', error);
            setAvailableAddresses([]);
            setVillages([]);
          } finally {
            setLoadingAddresses(false);
          }
          resetFormWithMemberData();
        };
        loadAllEstateAddresses();
      } else {
        // For non-estate members, reset form immediately
        resetFormWithMemberData();

        // Ensure the selects are populated even when not loading addresses
        const membershipTypeValue = (memberData as any).membershipType || undefined;
        const membershipStatusValue = (memberData as any).membershipStatus || 'active';
        const paymentStatusValue = (memberData as any).paymentStatus || 'current';

        setValue('membershipType', membershipTypeValue);
        setValue('membershipStatus', membershipStatusValue);
        setValue('paymentStatus', paymentStatusValue);

        // Ensure estateAddressId is cleared for non-estate members
        setValue('estateAddressId', '');
      }
    } catch (err: any) {
      console.error('Error loading member:', err);
      setError(err.response?.data?.message || 'Failed to load member');
    } finally {
      setInitialLoading(false);
    }
  }, [id, reset, setValue]);

  useEffect(() => {
    if (id) {
      loadMember();
    }
  }, [id, loadMember]);

  // Scroll to top when validation errors occur
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errors]);

  const validateFormData = (data: MemberFormData) => {
    const errors: string[] = [];

    // Only validate essential fields (email validation removed)
    if (!data.phone.mobile.trim()) errors.push('Mobile phone number is required');
    if (!data.idNumber?.trim()) errors.push('ID Number or Passport Number is required');

    return errors;
  };

  const onSubmit = async (data: MemberFormData) => {
    // Robust scroll to top when form is submitted
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;

    const validationErrors = validateFormData(data);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError('');

    // Set a frontend timeout as backup
    timeoutRef.current = setTimeout(() => {
      setError('Request timed out. Please try again.');
      setLoading(false);
    }, 35000); // 35 seconds - slightly longer than server timeout

    try {
      // Create data object that matches the backend expected format
      const updateData = {
        ...data,
        // Handle phone based on the new structure
        phone: data.phone.mobile,
        // Convert string 'true'/'false' to boolean
        isActive: data.isActive === 'true',
        // Map the client form endDate to the server-expected expiryDate
        expiryDate: data.endDate,
        // Map membership types if needed
        membershipType: data.membershipPosition === 'Outside Member' ? data.membershipType || 'Single' : data.membershipPosition,
        // Only include contractLength for Outside Members
        ...(data.membershipPosition === 'Outside Member' && data.contractLength && { contractLength: data.contractLength }),
        // Transform address back to object structure for server
        address: {
          street: data.address,
          city: '', // These will be parsed or left empty for now
          state: '',
          zipCode: '',
          country: 'South Africa',
        },
        // Include estate address ID if provided
        ...(data.estateAddressId && { estateAddressId: data.estateAddressId }),
        // Explicitly include new fields that might be missed
        idNumber: data.idNumber,
        employer: data.employer,
        businessTel: data.businessTel,
        occupation: data.occupation,
        spouse: data.spouse,
        // Fix children dateOfBirth null values
        children: (data.children || []).filter(child => child.name.trim() !== '').map(child => ({
          ...child,
          dateOfBirth: child.dateOfBirth == null ? '' : child.dateOfBirth
        })),
        debitOrder: data.debitOrder,
        medicalInfo: data.medicalInfo,
        legalAcknowledgements: data.legalAcknowledgements,
        // Include signature fields
        signedAt: data.signedAt,
        signedDate: data.signedDate,
        signedMonth: data.signedMonth,
        signature: data.signature,
        // Include notes with work phone if provided
        notes: data.phone.work ? `Work Phone: ${data.phone.work}\n${data.notes || ''}` : (data.notes || ''),
      };
      
      // Debug log to see what data is being sent
      console.log('Submitting data for update:', updateData);

      const response = await apiService.updateMember(id!, updateData);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      console.log('Update response:', response);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/members/${id}`);
      }, 2000);
    } catch (err: any) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      console.error('Error updating member:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!member) {
    return (
      <Box>
        <Alert severity="error">
          Member not found
        </Alert>
        <Button onClick={() => navigate('/members')} sx={{ mt: 2 }}>
          Back to Members
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
              Edit Member
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Update {member.firstName} {member.lastName}'s membership information
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            onClick={() => navigate(`/members/${id}`)}
          >
            Back to Member
          </Button>
        </Box>

        {/* Alert Messages */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': { fontWeight: 500 }
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': { fontWeight: 500 }
            }}
          >
            Member updated successfully! Redirecting...
          </Alert>
        )}

        {/* Main Form Container */}
        <Paper 
          elevation={4}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Form Header */}
          <Box 
            sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Alert 
              severity="info" 
              sx={{ 
                backgroundColor: 'transparent',
                border: 'none',
                '& .MuiAlert-icon': { color: 'primary.main' }
              }}
            >
              Update the member information below. All required fields must be filled to save changes.
            </Alert>
          </Box>
          
          <Box sx={{ p: 4 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Membership Details Section */}
              <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                          color: 'white',
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <PaymentIcon />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          1. Membership Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Configure membership type, rates, and payment terms
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    {/* Row 1: Membership Configuration */}
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="membershipPosition"
                        control={control}
                        rules={{ required: 'Membership position is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.membershipPosition} disabled={loading}>
                            <InputLabel>Membership Type</InputLabel>
                            <Select
                              {...field}
                              label="Membership Position"
                            >
                              <MenuItem value="Home Owner (H/O)">Home Owner (H/O)</MenuItem>
                              <MenuItem value="Tenant">Tenant</MenuItem>
                              <MenuItem value="Outside Member">Outside Member</MenuItem>
                            </Select>
                            {errors.membershipPosition && (
                              <FormHelperText>{errors.membershipPosition.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {currentMembershipPosition === 'Outside Member' ? (
                        <Controller
                          name="membershipType"
                          control={control}
                          rules={{ required: 'Membership type is required for Outside Members' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.membershipType} disabled={loading}>
                              <InputLabel>Membership Type</InputLabel>
                              <Select
                                {...field}
                                label="Membership Type"
                              >
                                <MenuItem value="Single">Single</MenuItem>
                                <MenuItem value="Family">Family</MenuItem>
                                <MenuItem value="Scholars (u18 yrs)">Scholars (u18 yrs)</MenuItem>
                                <MenuItem value="Parent and child (u18 yrs)">Parent and child (u18 yrs)</MenuItem>
                                <MenuItem value="Pensioner">Pensioner</MenuItem>
                                <MenuItem value="Police">Police</MenuItem>
                              </Select>
                              {errors.membershipType && <FormHelperText>{errors.membershipType.message}</FormHelperText>}
                            </FormControl>
                          )}
                        />
                      ) : (
                        <Box sx={{ 
                          height: '56px', 
                          display: 'flex', 
                          alignItems: 'center',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          pl: 2
                        }}>
                          
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                      {currentMembershipPosition === 'Outside Member' ? (
                        <Controller
                          name="contractLength"
                          control={control}
                          rules={{ required: 'Contract length is required for Outside Members' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.contractLength} disabled={loading}>
                              <InputLabel>Contract Length</InputLabel>
                              <Select
                                {...field}
                                label="Contract Length"
                              >
                                <MenuItem value="12 Months">12 Months</MenuItem>
                                <MenuItem value="6 Months">6 Months</MenuItem>
                                <MenuItem value="1 Month">1 Month</MenuItem>
                                <MenuItem value="2 Weeks">2 Weeks</MenuItem>
                              </Select>
                              {errors.contractLength && <FormHelperText>{errors.contractLength.message}</FormHelperText>}
                            </FormControl>
                          )}
                        />
                      ) : (
                        <Box sx={{ 
                          height: '56px', 
                          display: 'flex', 
                          alignItems: 'center',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          pl: 2
                        }}>
                          
                        </Box>
                      )}
                    </Grid>

                    {/* Row 2: Date and Rate Information */}
                    {/* Hide contract start date for Home Owner and Tenant */}
                    {currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant' && (
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="startDate"
                          control={control}
                          rules={{ required: 'Start date is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Contract Start Date"
                              type="date"
                              error={!!errors.startDate}
                              helperText={errors.startDate?.message}
                              fullWidth
                              disabled={loading}
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                '& input': { padding: '16.5px 14px' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    {/* Hide expiry date field for Home Owner and Tenant - automatically set to 50 years */}
                    {currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant' && (
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="endDate"
                          control={control}
                          rules={{ required: 'End date is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Membership Expiry Date"
                              type="date"
                              error={!!errors.endDate}
                              helperText={errors.endDate?.message}
                              fullWidth
                              disabled={loading}
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                '& input': { padding: '16.5px 14px' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    {currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant' && (
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="rate"
                        control={control}
                        rules={{ required: 'Rate is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={currentPaymentFrequency === 'Upfront' ? 'Rate (R)' : 'Monthly Rate (R)'}
                            type="number"
                            error={!!errors.rate}
                            fullWidth
                            disabled={loading}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                              startAdornment: 'R',
                              readOnly: false,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    )}
                    {/* Row 3: Payment Configuration and Status - Hidden for Home Owner and Tenant */}
                    {(currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant') && (
                      <>
                        {currentPaymentType !== 'EFT/Cash' && (
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="paymentDay"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth disabled={loading}>
                                <InputLabel>Payment Day</InputLabel>
                                <Select
                                  {...field}
                                  label="Payment Day"
                                  sx={{ borderRadius: 2 }}
                                >
                                  <MenuItem value="1st">1st of Month</MenuItem>
                                  <MenuItem value="25th">25th of Month</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Grid>
                        )}

                        <Grid item xs={12} md={4}>
                          <Controller
                            name="paymentType"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth disabled={loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                <InputLabel>Payment Type</InputLabel>
                                <Select
                                  {...field}
                                  label="Payment Type"
                                  sx={{ borderRadius: 2 }}
                                >
                                  <MenuItem value="Debit Order">Debit Order</MenuItem>
                                  <MenuItem value="EFT/Cash">EFT/Cash</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Grid>

                        {currentPaymentType === 'EFT/Cash' && (
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="paymentFrequency"
                            control={control}
                            render={({ field }) => {
                              // Simple payment options for EFT/Cash
                              const getPaymentOptions = () => {
                                return [
                                  { value: 'Monthly', label: 'Monthly' },
                                  { value: 'Upfront', label: 'Upfront' }
                                ];
                              };

                              const paymentOptions = getPaymentOptions();

                              return (
                                <FormControl fullWidth disabled={loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                  <InputLabel>Payment Option</InputLabel>
                                  <Select
                                    {...field}
                                    label="Payment Option"
                                    sx={{ borderRadius: 2 }}
                                    disabled={paymentOptions.length <= 1}
                                  >
                                    {paymentOptions.map(option => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              );
                            }}
                          />
                        </Grid>
                        )}
                      </>
                    )}

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="membershipStatus"
                        control={control}
                        rules={{ required: 'Membership status is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.membershipStatus} disabled={loading}>
                            <InputLabel>Membership Status</InputLabel>
                            <Select
                              {...field}
                              label="Membership Status"
                            >
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="inactive">Inactive</MenuItem>
                              <MenuItem value="suspended">Suspended</MenuItem>
                              <MenuItem value="expired">Expired</MenuItem>
                            </Select>
                            {errors.membershipStatus && (
                              <FormHelperText>{errors.membershipStatus.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* Row 4: Fees and Total Summary - hide for Home Owner and Tenant */}
                    {currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant' && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Controller
                            name="joiningFee"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Joining Fee (R)"
                                type="number"
                                fullWidth
                                disabled={loading}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                InputProps={{
                                  startAdornment: 'R',
                                }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Controller
                            name="proRata"
                            control={control}
                            render={({ field }) => (
                              <Box>
                                <TextField
                                  {...field}
                                  label="Pro-Rata Cost (R)"
                                  type="number"
                                  fullWidth
                                  disabled={loading}
                                  value={field.value || ''}
                                  onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  placeholder="0"
                                  InputProps={{
                                    startAdornment: 'R',
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  Pro-rata calculation for partial periods
                                </Typography>
                              </Box>
                            )}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={4}>
                      <Controller
                        name="paymentStatus"
                        control={control}
                        rules={{ required: 'Payment status is required' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.paymentStatus} disabled={loading}>
                            <InputLabel>Payment Status</InputLabel>
                            <Select
                              {...field}
                              label="Payment Status"
                            >
                              <MenuItem value="current">Current</MenuItem>
                              <MenuItem value="overdue">Overdue</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                            {errors.paymentStatus && (
                              <FormHelperText>{errors.paymentStatus.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
        
       

        
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PersonIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  2. Member's Particulars
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Personal information and contact details
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'First name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Last name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{}}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone.mobile"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Mobile phone number is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Mobile Phone"
                      fullWidth
                      error={!!errors.phone?.mobile}
                      helperText={errors.phone?.mobile?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone.work"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Work Phone"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  rules={{ required: 'Date of birth is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date of Birth"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.dateOfBirth}
                      helperText={errors.dateOfBirth?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="idNumber"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'ID Number/Passport is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="ID Number/Passport"
                      fullWidth
                      error={!!errors.idNumber}
                      helperText={errors.idNumber?.message}
                      disabled={loading}
                      onChange={(e) => {
                        const idNumber = e.target.value;
                        field.onChange(idNumber);

                        // Auto-populate date of birth if ID number is valid SA ID
                        if (idNumber.length === 13 && /^\d{13}$/.test(idNumber)) {
                          const dateOfBirth = extractDateOfBirthFromId(idNumber);
                          if (dateOfBirth) {
                            setValue('dateOfBirth', dateOfBirth);
                          }
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="employer.name"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Employer"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Address Information</Typography>
        <Grid container spacing={2}>
              {/* Conditional Address Fields based on Membership Position */}
              {currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant' ? (
                <>
                  <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                    <FormControl
                      fullWidth
                      error={!selectedVillage && (currentMembershipPosition === 'Home Owner (H/O)' || currentMembershipPosition === 'Tenant')}
                      disabled={loading}
                    >
                      <InputLabel>Village Name</InputLabel>
                      <Select
                        value={selectedVillage}
                        label="Village Name"
                        onChange={(e) => {
                          setSelectedVillage(e.target.value);
                          setValue('estateAddressId', ''); // Reset address selection when village changes
                        }}
                        disabled={loadingAddresses}
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>Select Village Name</em>
                        </MenuItem>
                        {villages.map((village) => (
                          <MenuItem key={village} value={village}>
                            {village}
                          </MenuItem>
                        ))}
                      </Select>
                      {loadingAddresses && (
                        <FormHelperText>Loading estates...</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                    <Controller
                      name="estateAddressId"
                      control={control}
                      rules={{ required: 'House number is required for Home Owners and Tenants' }}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!errors.estateAddressId}
                          disabled={loading || !selectedVillage || loadingAddresses}
                        >
                          <InputLabel>House Number</InputLabel>
                          <Select
                            {...field}
                            label="House Number"
                            fullWidth
                          >
                            <MenuItem value="">
                              <em>Select House Number</em>
                            </MenuItem>
                            {availableAddresses
                              .filter(address => !selectedVillage || address.villageName === selectedVillage)
                              .sort((a, b) => a.houseNumber - b.houseNumber)
                              .map((address) => (
                                <MenuItem key={address._id} value={address._id}>
                                  {address.houseNumber}
                                </MenuItem>
                              ))}
                          </Select>
                          {!selectedVillage && (
                            <FormHelperText>Please select a village name first</FormHelperText>
                          )}
                          {errors.estateAddressId && (
                            <FormHelperText>{errors.estateAddressId.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                </>
              ) : (
                /* Free-text Address Field for Outside Members */
                <Grid item xs={12}>
                  <Controller
                    name="address"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Physical Address"
                        fullWidth
                        multiline
                        rows={2}
                        disabled={loading}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="employer.address"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Business Address"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="businessTel"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Business Tel Number"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="occupation"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Occupation"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

      

        {/* Emergency Contact Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ContactEmergencyIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  3. Emergency Contact
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Emergency contact person details
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="emergencyContact.name"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Emergency contact name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      error={!!errors.emergencyContact?.name}
                      helperText={errors.emergencyContact?.name?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="emergencyContact.phone"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Emergency contact phone is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone"
                      fullWidth
                      error={!!errors.emergencyContact?.phone}
                      helperText={errors.emergencyContact?.phone?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="emergencyContact.relationship"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Relationship is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Relationship"
                      fullWidth
                      error={!!errors.emergencyContact?.relationship}
                      helperText={errors.emergencyContact?.relationship?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Spouse's Particulars Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FamilyRestroomIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  4. Spouse's Particulars
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Spouse or partner information
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="spouse.firstName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="spouse.lastName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="spouse.email"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="spouse.phone"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="spouse.idNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="ID Number"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="spouse.occupation"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Occupation"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Children's Particulars Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'info.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FamilyRestroomIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                  4. Children's Particulars
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add children information (if applicable) - Maximum 3 children
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {childrenFields.map((field, index) => (
              <Grid container spacing={3} key={field.id} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name={`children.${index}.name` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Name"
                        fullWidth
                        disabled={loading}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name={`children.${index}.dateOfBirth` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Date of Birth"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={loading}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          '& input': { padding: '16.5px 14px' }
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name={`children.${index}.idNumber` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="ID Number"
                        fullWidth
                        inputProps={{
                          maxLength: 13,
                          pattern: '[0-9]*'
                        }}
                        disabled={loading}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        onChange={(e) => {
                          const idNumber = e.target.value;
                          field.onChange(idNumber);

                          // Auto-populate date of birth if ID number is valid
                          if (idNumber.length === 13 && /^\d{13}$/.test(idNumber)) {
                            const extractDateOfBirthFromId = (idNumber: string): string => {
                              // Only extract date from SA ID numbers (13 digits)
                              if (!idNumber || idNumber.length !== 13 || !/^\d{13}$/.test(idNumber)) {
                                return '';
                              }

                              // Extract YYMMDD from the first 6 digits
                              const yearPart = idNumber.substring(0, 2);
                              const monthPart = idNumber.substring(2, 4);
                              const dayPart = idNumber.substring(4, 6);

                              // Convert 2-digit year to 4-digit year
                              // If year is 00-25, assume 2000-2025, otherwise assume 1900-1999
                              const century = parseInt(yearPart) <= 25 ? 2000 : 1900;
                              const fullYear = century + parseInt(yearPart);

                              // Validate the date components
                              const month = parseInt(monthPart);
                              const day = parseInt(dayPart);

                              if (month < 1 || month > 12 || day < 1 || day > 31) {
                                return '';
                              }

                              // Create date string in YYYY-MM-DD format
                              const dateString = `${fullYear}-${monthPart.padStart(2, '0')}-${dayPart.padStart(2, '0')}`;

                              // Validate that the date is valid
                              const date = new Date(dateString);
                              if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
                                return '';
                              }

                              return dateString;
                            };
                            
                            const dateOfBirth = extractDateOfBirthFromId(idNumber);
                            if (dateOfBirth) {
                              setValue(`children.${index}.dateOfBirth`, dateOfBirth);
                            }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    color="error"
                    onClick={() => removeChild(index)}
                    disabled={childrenFields.length === 1 || loading}
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Box sx={{ mt: 3 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => appendChild({ name: '', dateOfBirth: '', idNumber: '' })}
                variant="outlined"
                size="small"
                disabled={childrenFields.length >= 3 || loading}
                sx={{ borderRadius: 2 }}
              >
                Add Child {childrenFields.length >= 3 ? '(Maximum 3)' : `(${childrenFields.length}/3)`}
              </Button>
            </Box>
          </CardContent>
        </Card>


        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Additional Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={4}
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Debit Order Authority Section - Hidden for Home Owner and Tenant */}
        {(currentMembershipPosition !== 'Home Owner (H/O)' && currentMembershipPosition !== 'Tenant') && currentPaymentType !== 'EFT/Cash' && (
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PaymentIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  6. Debit Order Authority
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Banking details for automatic payments
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.accountHolder"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Account Holder"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="debitOrder.address"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Address"
                      fullWidth
                      multiline
                      rows={2}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.contactNumbers.cell"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cell Number"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.contactNumbers.work"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Work Number"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.bank"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bank"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.branchCode"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Branch Code"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.accountNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Account Number"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.accountType"
                  control={control}
                  defaultValue="Current"
                  render={({ field }) => (
                    <FormControl fullWidth disabled={loading}>
                      <InputLabel>Account Type</InputLabel>
                      <Select
                        {...field}
                        label="Account Type"
                      >
                        <MenuItem value="Current">Current</MenuItem>
                        <MenuItem value="Savings">Savings</MenuItem>
                        <MenuItem value="Transmission">Transmission</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.amountToDeduct"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Amount to Deduct"
                      type="number"
                      fullWidth
                      disabled={loading}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="debitOrder.debitOrderDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Debit Order Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        )}

        {/* Medical Info Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <MedicalServicesIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  7. Indemnity & Medical Info
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Medical information and emergency medical contacts
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="medicalInfo.allergies"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Allergies"
                      fullWidth
                      multiline
                      rows={2}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="medicalInfo.medication"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Medication"
                      fullWidth
                      multiline
                      rows={2}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="medicalInfo.recentSurgery"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Recent Surgery"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="medicalInfo.doctorName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Doctor's Name"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="medicalInfo.doctorNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Doctor's Number"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="medicalInfo.emergencyContact"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Emergency Contact"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Signatures Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DrawIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  8. Signatures
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Document signing information
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="signedAt"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Signed At"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="signedMonth"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Signed Month"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="signedDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Signed Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="signature"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Signature"
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Legal Acknowledgements Section */}
        <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <GavelIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  9. Legal Acknowledgements
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Required legal agreements and consents
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <FormControl component="fieldset" sx={{ mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="legalAcknowledgements.mandate"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="I authorize this mandate for debit order collection"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="legalAcknowledgements.cancellation"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="I acknowledge the cancellation policy"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="legalAcknowledgements.assignment"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="I agree to the assignment and cession"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="legalAcknowledgements.popiAct"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="I consent to the POPI Act provisions"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="legalAcknowledgements.indemnity"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} />}
                        label="I agree to the indemnity terms"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </CardContent>
        </Card>

        {/* Notes Section */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <NotesIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  10. Additional Notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add any additional information or special requirements
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Add any additional notes, special requirements, or important information about this member..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Divider sx={{ my: 4 }} />

        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pt: 3,
            mt: 4,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(`/members/${id}`)}
            disabled={loading}
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
            </form>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EditMember;
